import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessDenied, Button, ConfirmModal, Input, ModalShell } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type PanelUserRole = 'admin' | 'manager' | 'user';

type PanelUser = {
  id: number;
  username: string;
  email: string;
  role: PanelUserRole;
  isActive: boolean;
  twoFactorEnabled: boolean;
  createdAt: string;
};

function axiosMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
  }
  return err instanceof Error ? err.message : fallback;
}

export const PanelUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { canManageUsers, user: currentUser } = useAuth();
  const { success, error: showError } = useToast();
  const [users, setUsers] = useState<PanelUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PanelUser | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<PanelUserRole>('manager');
  const [resetTwoFactor, setResetTwoFactor] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirming, setConfirming] = useState<{
    user: PanelUser;
    action: 'toggle' | 'delete' | 'resetPassword';
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<PanelUser[]>('/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showError(axiosMessage(err, t('common.error')));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageUsers) return;
    void load();
  }, [canManageUsers]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return users;
    return users.filter((item) =>
      [item.username, item.email, item.role].some((value) => value.toLowerCase().includes(q))
    );
  }, [users, searchTerm]);

  const resetForm = () => {
    setEditing(null);
    setUsername('');
    setEmail('');
    setPassword('');
    setRole('manager');
    setResetTwoFactor(false);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (item: PanelUser) => {
    setEditing(item);
    setUsername(item.username);
    setEmail(item.email);
    setPassword('');
    setRole(item.role);
    setResetTwoFactor(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !email.trim()) {
      showError(t('pages.panelUsers.requiredFields'));
      return;
    }
    if (editing && password && password.length < 8) {
      showError(t('pages.panelUsers.passwordMin'));
      return;
    }

    setIsSaving(true);
    try {
      if (editing) {
        await httpClient.put(`/users/${editing.id}`, {
          username: username.trim(),
          email: email.trim(),
          role,
          ...(password ? { password } : {}),
          ...(resetTwoFactor ? { resetTwoFactor: true } : {}),
        });
        success(t('pages.panelUsers.updated'));
      } else {
        const res = await httpClient.post<{ sentTo?: string }>('/users', {
          username: username.trim(),
          email: email.trim(),
          role,
        });
        success(t('pages.panelUsers.createdSent', { email: res.data.sentTo || email.trim() }));
      }
      closeModal();
      await load();
    } catch (err) {
      showError(axiosMessage(err, t('common.error')));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirming) return;
    setIsConfirming(true);
    try {
      if (confirming.action === 'delete') {
        await httpClient.delete(`/users/${confirming.user.id}`);
        success(t('pages.panelUsers.deleted'));
      } else if (confirming.action === 'resetPassword') {
        const res = await httpClient.post<{ sentTo: string }>(
          `/users/${confirming.user.id}/reset-password`
        );
        success(t('pages.panelUsers.resetSent', { email: res.data.sentTo }));
      } else {
        await httpClient.put(`/users/${confirming.user.id}`, {
          isActive: !confirming.user.isActive,
        });
        success(
          confirming.user.isActive
            ? t('pages.panelUsers.deactivated')
            : t('pages.panelUsers.activated')
        );
      }
      setConfirming(null);
      await load();
    } catch (err) {
      showError(axiosMessage(err, t('common.error')));
    } finally {
      setIsConfirming(false);
    }
  };

  if (!canManageUsers) {
    return <AccessDenied />;
  }

  const roleLabel = (value: PanelUserRole) => t(`pages.panelUsers.roles.${value}`);

  return (
    <div className="panel-page h-full min-w-0 flex flex-col overflow-auto">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.panelUsers.title')}</h1>
          <p className="panel-page-subtitle">{t('pages.panelUsers.subtitle')}</p>
        </div>
        <Button type="button" size="sm" onClick={openCreate}>
          {t('pages.panelUsers.create')}
        </Button>
      </div>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 space-y-3">
        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('pages.panelUsers.searchPlaceholder')}
          className="h-8 py-1 text-xs"
          aria-label={t('common.search')}
        />

        {isLoading ? (
          <p className="px-2 py-6 text-sm text-slate-500">{t('common.loading')}</p>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-6 text-sm text-slate-500">{t('pages.panelUsers.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('auth.username')}</th>
                  <th className="px-3 py-2 font-medium">{t('auth.email')}</th>
                  <th className="px-3 py-2 font-medium">{t('pages.panelUsers.role')}</th>
                  <th className="px-3 py-2 font-medium">{t('pages.panelUsers.twoFactor')}</th>
                  <th className="px-3 py-2 font-medium">{t('pages.panelUsers.status')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const isSelf = currentUser?.id.getValue() === item.id;
                  return (
                    <tr key={item.id} className="border-t border-slate-100 dark:border-gray-700">
                      <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">
                        {item.username}
                        {isSelf ? (
                          <span className="ml-2 text-xs font-normal text-teal-700 dark:text-teal-300">
                            {t('pages.panelUsers.you')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{item.email}</td>
                      <td className="px-3 py-2">{roleLabel(item.role)}</td>
                      <td className="px-3 py-2">
                        {item.twoFactorEnabled ? t('common.yes') : t('common.no')}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            item.isActive
                              ? 'text-teal-700 dark:text-teal-300'
                              : 'text-slate-500'
                          }
                        >
                          {item.isActive ? t('pages.panelUsers.active') : t('pages.panelUsers.inactive')}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(item)}>
                            {t('common.edit')}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirming({ user: item, action: 'resetPassword' })}
                          >
                            {t('pages.panelUsers.resetPassword')}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirming({ user: item, action: 'toggle' })}
                            disabled={isSelf && item.isActive}
                          >
                            {item.isActive
                              ? t('pages.panelUsers.deactivate')
                              : t('pages.panelUsers.activate')}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirming({ user: item, action: 'delete' })}
                            disabled={isSelf}
                          >
                            {t('common.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ModalShell
        isOpen={showModal}
        onClose={closeModal}
        title={editing ? t('pages.panelUsers.editTitle') : t('pages.panelUsers.createTitle')}
        subtitle={t('pages.panelUsers.modalSubtitle')}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={closeModal} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="panel-user-form" isLoading={isSaving}>
              {editing ? t('common.save') : t('pages.panelUsers.create')}
            </Button>
          </div>
        }
      >
        <form id="panel-user-form" onSubmit={handleSave} className="space-y-3">
          <Input
            label={t('auth.username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoFocus
          />
          <Input
            label={t('auth.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {editing ? (
            <Input
              label={t('pages.panelUsers.newPassword')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('pages.panelUsers.passwordKeep')}
            />
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('pages.panelUsers.createEmailHint')}
            </p>
          )}
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('pages.panelUsers.role')}
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as PanelUserRole)}
              className="mt-1 block h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="admin">{t('pages.panelUsers.roles.admin')}</option>
              <option value="manager">{t('pages.panelUsers.roles.manager')}</option>
              <option value="user">{t('pages.panelUsers.roles.user')}</option>
            </select>
          </label>
          {editing?.twoFactorEnabled ? (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={resetTwoFactor}
                onChange={(e) => setResetTwoFactor(e.target.checked)}
              />
              {t('pages.panelUsers.resetTwoFactor')}
            </label>
          ) : null}
        </form>
      </ModalShell>

      <ConfirmModal
        isOpen={Boolean(confirming)}
        onClose={() => setConfirming(null)}
        onConfirm={handleConfirm}
        variant={confirming?.action === 'delete' ? 'danger' : 'primary'}
        isLoading={isConfirming}
        title={
          confirming?.action === 'delete'
            ? t('pages.panelUsers.confirmDeleteTitle')
            : confirming?.action === 'resetPassword'
              ? t('pages.panelUsers.confirmResetTitle')
              : confirming?.user.isActive
                ? t('pages.panelUsers.confirmDeactivateTitle')
                : t('pages.panelUsers.confirmActivateTitle')
        }
        message={
          confirming?.action === 'delete'
            ? t('pages.panelUsers.confirmDeleteMessage', { name: confirming.user.username })
            : confirming?.action === 'resetPassword'
              ? t('pages.panelUsers.confirmResetMessage', {
                  name: confirming.user.username,
                  email: confirming.user.email,
                })
              : confirming?.user.isActive
                ? t('pages.panelUsers.confirmDeactivateMessage', { name: confirming?.user.username })
                : t('pages.panelUsers.confirmActivateMessage', { name: confirming?.user.username })
        }
      />
    </div>
  );
};
